import { IList }                     from '@app/core/interfaces/list-response.interfaces';
import { Address }                   from '@app/shared/components/address/models/address.model';
import { RequestState }              from '@core/interfaces/api.interface';
import { IOrganizationEntityEvents } from '@core/interfaces/entity.interface';
import { EntityErrors }              from '@models/entity.model';
import { NaturalPerson }             from '@shared/models/natural-person.model';

// ENUMS #############################################################
// API enums *********************************************************
/**
 * Key to ID numbers of company types
 *
 * 16	Person mit Beteiligungen an gewerbl. Personengesellschaften,
 * 20	Atypische stille Gesellschaft,
 * 21	Offene Handelsgesellschaft,
 * 22	Kommanditgesellschaft,
 * 23	GmbH u. Co. KG,
 * 24	GmbH u. Co. OHG,
 * 25	Aktiengesellschaft u. Co. KG,
 * 26	Aktiengesellschaft u. Co. OHG,
 * 27	Gesellschaft des bürgerlichen Rechts,
 * 28	Europäische wirtschaftliche Interessenvereinigung,
 * 29	Sonstige Personengesellschaft (z.B. Grundstücksgemeinschaft o.Ä.),
 * 30 Eingetragene Genossenschaft (eG)
 * 31	Aktiengesellschaft,
 * 32	Kommanditgesellschaft auf Aktien,
 * 33	Kolonialgesellschaft,
 * 34	Bergrechtliche Gewerkschaft,
 * 35	Gesellschaft mit beschränkter Haftung,
 * 36	Europäische Aktiengesellschaft,
 * 37	Unternehmergesellschaft (haftungsbeschränkt),
 * 38 Gemeinnützige Gesellschaft mit beschränkter Haftung (gGmbH)
 * 39	Sonstige Kapitalgesellschaft,
 * 40 Stiftung & Co. KG
 * 41	Kreditgenossenschaft, die Kredite ausschließlich an ihre Mitglieder gewährt,
 * 42	Zentralkasse, die Kredite ausschließlich an ihre Mitglieder gewährt und sich auf genossenschaftl. Aufgaben beschränkt,
 * 43	Landwirtschaftl. Nutzungs- oder Verwaltungsgenossenschaft,
 * 44	Realgemeinde,
 * 45	SCE Europäische Genossenschaft,
 * 49	Andere Erwerbs- oder Wirtschaftsgenossenschaft,
 * 51	Versicherungsverein auf Gegenseitigkeit,
 * 52	Eingetragener Verein,
 * 59	Sonstige juristische Person des privaten Rechts,
 * 61	Nichtrechtsfähiger Verein, Anstalt, Stiftung oder anderes Zweckvermögen,
 * 71	Staatsbank,
 * 72	Öffentliche oder unter Staatsaufsicht stehende Sparkasse,
 * 73	Sonstige Kreditanstalt des öffentlichen Rechts,
 * 74	Öffentl.-rechtl. Versorgungs-, Verkehrs- oder Hafenbetrieb,
 * 79	Sonstiger Betrieb gewerblicher Art von jur. Personen des öffentlichen Rechts,
 * 81	Gebietskörperschaft,
 * 82	Öffentl.-rechtl. Religionsgesellschaft,
 * 83	Sonstige juristische Personen des öffentlichen Rechts,
 * 91	Ausländische Körperschaft,
 * 92	Ausländische Personengesellschaft,
 * 99	Sonstige ausländische Rechtsform
 */
export enum LegalFormEnum {
	LF_16 = '16',
	LF_20 = '20',
	LF_21 = '21',
	LF_22 = '22',
	LF_23 = '23',
	LF_24 = '24',
	LF_25 = '25',
	LF_26 = '26',
	LF_27 = '27',
	LF_28 = '28',
	LF_29 = '29',
	LF_30 = '30',
	LF_31 = '31',
	LF_32 = '32',
	LF_33 = '33',
	LF_34 = '34',
	LF_35 = '35',
	LF_36 = '36',
	LF_37 = '37',
	LF_38 = '38',
	LF_39 = '39',
	LF_40 = '40',
	LF_41 = '41',
	LF_42 = '42',
	LF_43 = '43',
	LF_44 = '44',
	LF_45 = '45',
	LF_49 = '49',
	LF_51 = '51',
	LF_52 = '52',
	LF_59 = '59',
	LF_61 = '61',
	LF_71 = '71',
	LF_72 = '72',
	LF_73 = '73',
	LF_74 = '74',
	LF_79 = '79',
	LF_81 = '81',
	LF_82 = '82',
	LF_83 = '83',
	LF_91 = '91',
	LF_92 = '92',
	LF_99 = '99'
}


export enum OrganizationStatusEnum {
	ACTIVE = 'active',
	DELETED = 'deleted',
	DISABLED = 'disabled'
}

export enum VatIdCountryCodeEnum {
	AT = 'Austria',
	BE = 'Belgium',
	BG = 'Bulgaria',
	CY = 'Cyprus',
	CZ = 'Czech Republic',
	DE = 'Germany',
	DK = 'Denmark',
	EE = 'Estonia',
	EL = 'Greece', // Note that - Greece is EL and not GR ! ;-)
	ES = 'Spain',
	FI = 'Finland',
	FR = 'France',
	HR = 'Croatia',
	HU = 'Hungary',
	IE = 'Ireland',
	IT = 'Italy',
	LT = 'Lithuania',
	LU = 'Luxembourg',
	LV = 'Latvia',
	MT = 'Malta',
	NL = 'Netherlands',
	PL = 'Poland',
	PT = 'Portugal',
	RO = 'Romania',
	SE = 'Sweden',
	SI = 'Slovenia',
	SK = 'Slovakia',
	XI = 'Northern Ireland'
}

// UI enums *********************************************************


// INTERFACES ########################################################
// API interfaces ****************************************************
// UI interfaces *****************************************************


// TYPES #############################################################
export type OrganizationProp = ( keyof Organization );
export type OrganizationProps = OrganizationProp[];

// ENTITIES ##########################################################
// MODEL USED FOR TRANSITION APP<->API (TransitionalModelObject)

export class OrganizationTMO {
	public addressData: Address = new Address();
	public createdBy?: string = undefined;
	public createdDate?: Date = undefined;
	public detailedDescription: string = undefined;
	public businessIdentificationNumber?: string = undefined;
	public errors?: EntityErrors = new EntityErrors();
	public hasChildren: boolean = undefined;
	public id: string = undefined;
	public identificationNumber: string = undefined;
	public lastModifiedBy?: string = undefined;
	public lastModifiedDate?: Date = undefined;
	public legalForm: LegalFormEnum = undefined;
	public name: string = undefined;
	public naturalPerson: NaturalPerson = new NaturalPerson();
	public organizationIdentifier: string = undefined;
	public parentOrgId: string = undefined;
	public status: OrganizationStatusEnum = undefined;
	public taxableEntity: boolean = undefined;
	public taxNumber: string = undefined;
	public vatId: string = undefined;
	public website: string = undefined;
}

export class Organization extends OrganizationTMO {
	public etag?: string = undefined;
	public children?: IList<Organization> = undefined;
	public hasChildren: boolean = undefined;
	public ui?: IOrganizationEntityEvents = {
		requestState: RequestState.NOT_ASKED,
		requestError: undefined,
		entityActions: {}
	};
}
